/** Spring 6-field cron: second minute hour day-of-month month day-of-week */

export type SchedulePreset =
  | "none"
  | "everyMinute"
  | "every15Minutes"
  | "every30Minutes"
  | "hourly"
  | "daily"
  | "weekly"
  | "monthly"
  | "yearly"
  | "custom";

export type ScheduleConfig = {
  preset: SchedulePreset;
  minute?: number;
  hour?: number;
  dayOfMonth?: number;
  dayOfWeek?: string;
  customCron?: string;
};

const DOW_NAMES = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"] as const;

export const SCHEDULE_DAY_OF_WEEK_OPTIONS = DOW_NAMES.map((value) => ({ value }));

export function clampMinute(value: number): number {
  return Math.min(59, Math.max(0, Math.round(value)));
}

export function clampHour(value: number): number {
  return Math.min(23, Math.max(0, Math.round(value)));
}

export function clampDayOfMonth(value: number): number {
  return Math.min(28, Math.max(1, Math.round(value)));
}

export function normalizeCron(cron: string): string {
  return cron.trim().replace(/\s+/g, " ");
}

export function parseScheduleCron(cron: string | undefined | null): ScheduleConfig {
  const normalized = normalizeCron(cron ?? "");
  if (!normalized) {
    return { preset: "none", minute: 0, hour: 0, dayOfMonth: 1, dayOfWeek: "MON" };
  }

  if (normalized === "0 * * * * *") {
    return { preset: "everyMinute", minute: 0, hour: 0, dayOfMonth: 1, dayOfWeek: "MON" };
  }

  if (normalized === "0 */15 * * * *") {
    return { preset: "every15Minutes", minute: 0, hour: 0, dayOfMonth: 1, dayOfWeek: "MON" };
  }

  if (normalized === "0 */30 * * * *") {
    return { preset: "every30Minutes", minute: 0, hour: 0, dayOfMonth: 1, dayOfWeek: "MON" };
  }

  const hourly = normalized.match(/^0 (\d{1,2}) \* \* \* \*$/);
  if (hourly) {
    return {
      preset: "hourly",
      minute: clampMinute(Number(hourly[1])),
      hour: 0,
      dayOfMonth: 1,
      dayOfWeek: "MON",
    };
  }

  const yearly = normalized.match(/^0 (\d{1,2}) (\d{1,2}) 1 1 \*$/);
  if (yearly) {
    return {
      preset: "yearly",
      minute: clampMinute(Number(yearly[1])),
      hour: clampHour(Number(yearly[2])),
      dayOfMonth: 1,
      dayOfWeek: "MON",
    };
  }

  const daily = normalized.match(/^0 (\d{1,2}) (\d{1,2}) \* \* \*$/);
  if (daily) {
    return {
      preset: "daily",
      minute: clampMinute(Number(daily[1])),
      hour: clampHour(Number(daily[2])),
      dayOfMonth: 1,
      dayOfWeek: "MON",
    };
  }

  const weekly = normalized.match(/^0 (\d{1,2}) (\d{1,2}) \* \* ([A-Z0-9]+)$/);
  if (weekly) {
    const dow = weekly[3].toUpperCase();
    const dayOfWeek = DOW_NAMES.includes(dow as (typeof DOW_NAMES)[number]) ? dow : "MON";
    return {
      preset: "weekly",
      minute: clampMinute(Number(weekly[1])),
      hour: clampHour(Number(weekly[2])),
      dayOfMonth: 1,
      dayOfWeek,
    };
  }

  const monthly = normalized.match(/^0 (\d{1,2}) (\d{1,2}) (\d{1,2}) \* \*$/);
  if (monthly) {
    return {
      preset: "monthly",
      minute: clampMinute(Number(monthly[1])),
      hour: clampHour(Number(monthly[2])),
      dayOfMonth: clampDayOfMonth(Number(monthly[3])),
      dayOfWeek: "MON",
    };
  }

  return {
    preset: "custom",
    customCron: normalized,
    minute: 0,
    hour: 0,
    dayOfMonth: 1,
    dayOfWeek: "MON",
  };
}

export function buildScheduleCron(config: ScheduleConfig): string {
  const minute = clampMinute(config.minute ?? 0);
  const hour = clampHour(config.hour ?? 0);
  const dayOfMonth = clampDayOfMonth(config.dayOfMonth ?? 1);
  const dayOfWeek = config.dayOfWeek ?? "MON";

  switch (config.preset) {
    case "none":
      return "";
    case "everyMinute":
      return "0 * * * * *";
    case "every15Minutes":
      return "0 */15 * * * *";
    case "every30Minutes":
      return "0 */30 * * * *";
    case "hourly":
      return `0 ${minute} * * * *`;
    case "daily":
      return `0 ${minute} ${hour} * * *`;
    case "weekly":
      return `0 ${minute} ${hour} * * ${dayOfWeek}`;
    case "monthly":
      return `0 ${minute} ${hour} ${dayOfMonth} * *`;
    case "yearly":
      return `0 ${minute} ${hour} 1 1 *`;
    case "custom":
      return normalizeCron(config.customCron ?? "");
    default:
      return "";
  }
}

export function timeInputValue(hour: number, minute: number): string {
  return `${String(clampHour(hour)).padStart(2, "0")}:${String(clampMinute(minute)).padStart(2, "0")}`;
}

export function parseTimeInput(value: string): { hour: number; minute: number } {
  const [h, m] = value.split(":");
  return {
    hour: clampHour(Number(h)),
    minute: clampMinute(Number(m)),
  };
}
